import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { mockVirtualUid } from '@/hooks/useAppConfig'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface VerifyListing {
  id: string
  title: string
  status: string
  description?: string
  imageUrl?: string
}

interface VerifyResult {
  valid: boolean
  uid?: string
  listing?: VerifyListing
  error?: string
  message?: string
}

/**
 * Consumer "verify authenticity" page. With no chip to tap, the item links here with its
 * virtual UID (or listingId) and we call the public /v endpoint — which, in mock mode, returns
 * the bound listing as authentic without cryptographic checks.
 */
export default function VerifyPage() {
  const [params] = useSearchParams()
  const [result, setResult] = useState<VerifyResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const listingId = params.get('listingId')
  const uid = params.get('uid') || (listingId ? mockVirtualUid(listingId) : null)
  const ctr = params.get('ctr') || '000001'
  const cmac = params.get('cmac') || '0000000000000000'

  useEffect(() => {
    if (!uid) {
      setError('Missing tag identifier')
      setLoading(false)
      return
    }
    fetch(`${API_BASE_URL}/v?uid=${encodeURIComponent(uid)}&ctr=${ctr}&cmac=${cmac}`)
      .then(res => res.json())
      .then((data: VerifyResult) => setResult(data))
      .catch(() => setError('Verification request failed'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  const authentic = !!result?.valid

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
        {loading ? (
          <div className="py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Verifying authenticity…</p>
          </div>
        ) : error ? (
          <div className="py-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification unavailable</h1>
            <p className="text-gray-600">{error}</p>
          </div>
        ) : authentic ? (
          <div>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentic ✓</h1>
            <p className="text-gray-600 mb-6">This item's certificate of authenticity is valid.</p>

            {result?.listing && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                {result.listing.imageUrl && (
                  <img src={result.listing.imageUrl} alt={result.listing.title}
                       className="w-full h-40 object-cover rounded-md mb-3" />
                )}
                <div className="font-semibold text-gray-900">{result.listing.title}</div>
                {result.listing.description && (
                  <p className="text-sm text-gray-600 mt-1">{result.listing.description}</p>
                )}
                <div className="text-xs text-gray-500 mt-2">Status: {result.listing.status}</div>
              </div>
            )}

            <div className="text-xs text-gray-400 mt-4">Tag UID: {result?.uid}</div>
          </div>
        ) : (
          <div className="py-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Could not verify</h1>
            <p className="text-gray-600">{result?.error || result?.message || 'This tag could not be verified.'}</p>
          </div>
        )}
      </div>
    </div>
  )
}
